import { GetServerSideProps, NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useMount } from 'react-use'
import { DeltaStatic } from 'quill'

import { getLocale } from '@/lib/i18n-utils'
import { Gender } from '@/constants/gender'
import { SolidBackgroundVideo } from '@/components/tools/solid-background-video/Editor'
import {
  Orientation,
  ScriptType,
  SOLID_BACKGROUND_VIDEO_GENERATE_STATE,
  TextScriptTempo,
} from '@/constants/tools/solid-background-video'
import { findOneToolsSolidBackgroundVideoJob } from '@/lib/tools/db/queries/tools-solid-background-video-jobs'

type PageProps<TProps extends {}> = {
  pageProps: TProps
}

type SolidBackgroundVideoPageProps = {
  redirected?: {
    projectName: string
    voice: {
      modelId: string
      voiceId: string
      language: string
      gender: Gender
      isOriginalVoice: boolean
      type: string
    }
    orientation: Orientation
    backgroundColor: string
    modelPosition: {
      left: number
      top: number
      scaleX: number
      scaleY: number
    }

    scriptType: ScriptType

    textScriptDelta?: Partial<DeltaStatic>
    textScriptHtml?: string
    dictionary?: Record<string, string>
    tempo?: TextScriptTempo

    audioScriptName?: string
    audioScriptUrl?: string

    error: string
  }
}

export const getServerSideProps: GetServerSideProps<
  SolidBackgroundVideoPageProps
> = async (context) => {
  const locale = await getLocale(context)

  const query = context.query

  const redirected: {
    redirected?: {
      projectName: string
      voice: {
        modelId: string
        voiceId: string
        language: string
        gender: Gender
        isOriginalVoice: boolean
        type: string
      }
      orientation: Orientation
      backgroundColor: string
      modelPosition: {
        left: number
        top: number
        scaleX: number
        scaleY: number
      }

      scriptType: ScriptType

      textScriptDelta?: Partial<DeltaStatic>
      textScriptHtml?: string
      dictionary?: Record<string, string>
      tempo?: TextScriptTempo

      audioScriptName?: string
      audioScriptUrl?: string

      error: string
    }
  } = {}

  /**
   * 프로젝트 생성 중 오류가 발생하면 이 페이지로 이동됨\
   * 그 때 jobId를 함께 넘겨 받음\
   * 해당 job을 조회하여 이전에 입력된 값들, error를 클라이언트로 전달
   */
  const jobId = Array.isArray(query.jobId) ? query.jobId[0] : query.jobId

  if (!!jobId) {
    const foundJob = await findOneToolsSolidBackgroundVideoJob(jobId)

    if (
      !!foundJob &&
      foundJob.generateState === SOLID_BACKGROUND_VIDEO_GENERATE_STATE.ERROR
    ) {
      redirected.redirected = {
        projectName: foundJob.projectName,
        voice: foundJob.voice,
        orientation: foundJob.orientation,
        backgroundColor: foundJob.backgroundColor,
        modelPosition: foundJob.modelPosition,
        scriptType: foundJob.scriptType,
        error: foundJob.error ?? '',
      }

      if (!!foundJob.textScriptDelta) {
        redirected.redirected.textScriptDelta = foundJob.textScriptDelta
      }

      if (!!foundJob.textScriptHtml) {
        redirected.redirected.textScriptHtml = foundJob.textScriptHtml
      }

      if (!!foundJob.dictionary) {
        redirected.redirected.dictionary = foundJob.dictionary
      }

      if (!!foundJob.tempo) {
        redirected.redirected.tempo = foundJob.tempo
      }

      if (!!foundJob.audioScriptName) {
        redirected.redirected.audioScriptName = foundJob.audioScriptName
      }

      if (!!foundJob.audioScriptFile?.publicUrl) {
        redirected.redirected.audioScriptUrl =
          foundJob.audioScriptFile?.publicUrl
      }
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      ...redirected,
    },
  }
}

const SolidBackgroundVideoPage: NextPage<
  PageProps<SolidBackgroundVideoPageProps>
> = ({ pageProps: { redirected } }) => {
  /**
   * 쿼리 지우기
   */
  const router = useRouter()

  useMount(() => {
    router.replace('', undefined, { shallow: true })
  })

  return (
    <>
      <Head>
        <title>AI STUDIOS - Solid Background Video</title>
      </Head>

      <SolidBackgroundVideo redirected={redirected} />
    </>
  )
}

export default SolidBackgroundVideoPage
